import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { JOB_LIST_DEFAULTS } from '../constants/jobListDefaults';
import { useWorkOrderJobTypes } from '../hooks/useWorkOrderJobTypes';
import { useWorkOrderPriorities } from '../hooks/useWorkOrderPriorities';
import { PriorityFilterList } from './PriorityFilterList';

export function JobListFilters() {
  const [filters, setFilters] = useUrlFilters(JOB_LIST_DEFAULTS);
  const { data: jobTypesData } = useWorkOrderJobTypes();
  const { data: prioritiesData, isLoading: prioritiesLoading } = useWorkOrderPriorities();

  const selectedPriorities =
    filters.priority_id === 'all' ? [] : filters.priority_id.split(',').filter(Boolean);

  const [searchInput, setSearchInput] = useState(filters.search_term);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Sync local input when filters change externally.
  useEffect(() => {
    setSearchInput(filters.search_term);
  }, [filters.search_term]);

  // Push search to URL once the debounce settles — the searchInput check avoids
  // re-applying a stale value right after the input was cleared.
  useEffect(() => {
    if (debouncedSearch === searchInput && debouncedSearch !== filters.search_term) {
      setFilters({ search_term: debouncedSearch, page: '1' });
    }
  }, [debouncedSearch, searchInput, filters.search_term, setFilters]);

  const handleSearchChange = (value: string) => setSearchInput(value);

  const handlePriorityChange = (next: string[]) => {
    setFilters({ priority_id: next.length > 0 ? next.join(',') : 'all', page: '1' });
  };

  const handleJobTypeChange = (value: string) => {
    setFilters({ job_category_id: value, page: '1' });
  };

  const handleReset = () => {
    setSearchInput('');
    setFilters({ priority_id: 'all', job_category_id: 'all', search_term: '', page: '1' });
  };

  const hasActiveFilters =
    filters.priority_id !== 'all' ||
    filters.job_category_id !== 'all' ||
    filters.search_term !== '';

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search jobs..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 h-10 w-[260px]"
        />
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-10 w-[150px] justify-between font-normal">
            <span className="truncate">
              {selectedPriorities.length === 0
                ? 'All Priorities'
                : `${selectedPriorities.length} selected`}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-3" align="start">
          <PriorityFilterList
            isLoading={prioritiesLoading}
            data={prioritiesData}
            selected={selectedPriorities}
            onChange={handlePriorityChange}
          />
        </PopoverContent>
      </Popover>

      <Select value={filters.job_category_id} onValueChange={handleJobTypeChange}>
        <SelectTrigger className="h-10 w-[160px]">
          <SelectValue placeholder="All Job Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Job Types</SelectItem>
          {jobTypesData?.map((type) => (
            <SelectItem key={type.id} value={String(type.id)}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          onClick={handleReset}
          className="h-10 gap-1.5 text-muted-foreground"
        >
          <X className="w-4 h-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
